import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation, EventEmitter, Output, HostListener } from '@angular/core';
import { PhxTagSize, PhxTagType, PhxTagContrast, PhxTagCustomColor, PhxTagCustomColorType, PhxTag } from './phx-tag.types';
import mixinDisabled from '../../mixins/disabled';

const baseMixin = mixinDisabled(
  class {
    constructor() {}
  }
);

export const defaultValues: Omit<PhxTag, 'label'> = {
  size: 'medium',
  tagType: 'neutral',
  contrast: 'low',
  suffixIcon: null,
  disabled: false,
  customColor: null,
  customColorType: 'subtle',
};

@Component({
  selector: 'phx-tag',
  styleUrls: ['./phx-tag.scss'],
  templateUrl: './phx-tag.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  inputs: [
    'disabled'
  ],
  host: {
    class: 'phx-tag',
    '[class.phx-tag-type-accent]': `!phxCustomColor && phxTagType === 'accent'`,
    '[class.phx-tag-type-neutral]': `!phxCustomColor && phxTagType === 'neutral'`,
    '[class.phx-tag-type-positive]': `!phxCustomColor && phxTagType === 'positive'`,
    '[class.phx-tag-type-negative]': `!phxCustomColor && phxTagType === 'negative'`,
    '[class.phx-tag-type-caution]': `!phxCustomColor && phxTagType === 'caution'`,
    '[class.phx-tag-type-warning]': `!phxCustomColor && phxTagType === 'warning'`,
    '[class.phx-tag-custom-color-grey]': `phxCustomColor === 'grey'`,
    '[class.phx-tag-custom-color-blue]': `phxCustomColor === 'blue'`,
    '[class.phx-tag-custom-color-teal]': `phxCustomColor === 'teal'`,
    '[class.phx-tag-custom-color-green]': `phxCustomColor === 'green'`,
    '[class.phx-tag-custom-color-yellow]': `phxCustomColor === 'yellow'`,
    '[class.phx-tag-custom-color-orange]': `phxCustomColor === 'orange'`,
    '[class.phx-tag-custom-color-red]': `phxCustomColor === 'red'`,
    '[class.phx-tag-custom-color-magenta]': `phxCustomColor === 'magenta'`,
    '[class.phx-tag-custom-color-purple]': `phxCustomColor === 'purple'`,
    '[class.phx-tag-custom-color-indigo]': `phxCustomColor === 'indigo'`,
    '[class.phx-tag-custom-color]': '!!phxCustomColor',
    '[class.phx-tag-custom-color-type-subtle]': `!!phxCustomColor && phxCustomColorType === 'subtle'`,
    '[class.phx-tag-custom-color-type-medium]': `!!phxCustomColor && phxCustomColorType === 'medium'`,
    '[class.phx-tag-custom-color-type-bold]': `!!phxCustomColor && phxCustomColorType === 'bold'`,
    '[class.phx-tag-low-contrast]': `phxContrast === 'low'`,
    '[class.phx-tag-high-contrast]': `phxContrast === 'high'`,
    '[class.phx-has-suffix]': '!!phxSuffixIcon',
    '[class.phx-tag-lg]': `phxSize === 'large'`,
    '[class.phx-tag-md]': `phxSize === 'medium'`,
    '[class.phx-tag-sm]': `phxSize === 'small'`,
    '[class.phx-tag-ex-sm]': `phxSize === 'extra-small'`,
    '[class.phx-disabled]': `disabled`,
    '[attr.tabindex]': 'disabled ? -1 : 0',
  },
})
export class PhxTagComponent extends baseMixin {
  @Input() public phxSize: PhxTagSize = defaultValues.size;
  @Input() public phxTagType: PhxTagType = defaultValues.tagType;
  @Input() public phxContrast: PhxTagContrast = defaultValues.contrast;
  @Input() public phxSuffixIcon: string = defaultValues.suffixIcon;
  @Input() public phxCustomColor: PhxTagCustomColor = defaultValues.customColor;
  @Input() public phxCustomColorType: PhxTagCustomColorType = defaultValues.customColorType;
  @Output() public phxOnClick: EventEmitter<MouseEvent | KeyboardEvent> = new EventEmitter<MouseEvent | KeyboardEvent>();

  constructor() {
    super();
  }

  @HostListener("click", ["$event"])
  @HostListener("keydown.space", ["$event"])
  @HostListener("keydown.enter", ["$event"])
  public handleClick(event: MouseEvent | KeyboardEvent): void {
    if (this.disabled || this.phxDisabled) { return; }
    this.phxOnClick.emit(event);
  }
}
